/**
 * Metadatový katalog eChannels Tools operací (CSOB eChannels konektor). 21 operací
 * v 7 skupinách (Activation / Authorization / OTP / Profile / Device replacement /
 * Transaction / Display PIN). `subject` + `accessTokenOverride` injektuje stránka
 * (platí pro všechny operace), token spravuje backend. `buildEchannelsBody` řeší
 * jen pole operace. Port legacy `echannels-tools.js` collectFormData().
 */

export type EchFieldKind =
  | "text"
  | "textarea"
  | "number"
  | "select"
  | "uuid"
  | "tokenSelect"
  | "cacheGuid"
  | "pinRadio"
  | "password";

export interface EchFieldSpec {
  name: string;
  /** i18n klíč labelu. */
  label: string;
  kind: EchFieldKind;
  required?: boolean;
  default?: string;
  placeholder?: string;
  /** text/number: prázdno → `null` (jinak "" resp. fallback). */
  nullable?: boolean;
  /** number: prázdno → tato hodnota. */
  fallback?: number;
  /** select: pevné volby (value + i18n label klíč nebo přímý text). */
  options?: { value: string; label: string }[];
  /** tokenSelect: jen ACTIVE tokeny. */
  activeOnly?: boolean;
}

export type EchGroup = "activation" | "authorization" | "otp" | "profile" | "deviceReplacement" | "transaction" | "displayPin";

export interface EchOperationSpec {
  id: string;
  group: EchGroup;
  /** i18n klíč. */
  label: string;
  /** Podnadpis (downstream CSOB REST cesta, přímý text). */
  subtitle?: string;
  fields: EchFieldSpec[];
  /** Skutečná execute operace, pokud se liší od `id` (confirmAuthRequest → confirmTransactionWithOtp). */
  execAs?: string;
  /** cacheGuid pole auto-vyplní transactionId/signId/usedDeviceAuthType z cache (rejectAuthRequest). */
  autofillFromCache?: boolean;
  /** Operace vyžaduje tokenId z cache (selectedTokenId z getAuthList) — confirmAuthRequest. */
  needsCacheToken?: boolean;
}

export interface EchGroupSpec {
  group: EchGroup;
  label: string;
  operations: EchOperationSpec[];
}

const REASON_OPTIONS = [
  { value: "ACTIVATION", label: "ACTIVATION" },
  { value: "REACTIVATION", label: "REACTIVATION" },
  { value: "MIGRATION", label: "MIGRATION" },
];
const BOOL_OPTIONS = [
  { value: "false", label: "soaptools.opt.no" },
  { value: "true", label: "soaptools.opt.yes" },
];
const DEVICE_AUTH_OPTIONS = [
  { value: "0", label: "0" },
  { value: "1", label: "1" },
  { value: "2", label: "2" },
];

const ACTIVATION_OPERATIONS: EchOperationSpec[] = [
  {
    id: "initActivation",
    group: "activation",
    label: "echtools.op.initActivation",
    subtitle: "POST /v1/init",
    fields: [
      { name: "activationOtp", label: "echtools.f.activationOtp", kind: "text", required: true },
      { name: "activationGuid", label: "echtools.f.activationGuid", kind: "uuid", required: true },
      { name: "instanceId", label: "echtools.f.instanceId", kind: "text", nullable: true },
      { name: "reason", label: "echtools.f.reason", kind: "select", default: "ACTIVATION", options: REASON_OPTIONS },
    ],
  },
  {
    id: "getInstanceInfo",
    group: "activation",
    label: "echtools.op.getInstanceInfo",
    subtitle: "GET /v1/devices/{instanceId}",
    fields: [{ name: "tokenId", label: "echtools.f.tokenId", kind: "tokenSelect", required: true }],
  },
  {
    id: "finishActivation",
    group: "activation",
    label: "echtools.op.finishActivation",
    subtitle: "POST /v1/finish",
    fields: [
      { name: "mobileSDKData", label: "echtools.f.mobileSDKData", kind: "textarea", nullable: true },
      { name: "isMigration", label: "echtools.f.isMigration", kind: "select", default: "false", options: BOOL_OPTIONS },
    ],
  },
  {
    id: "processActivation",
    group: "activation",
    label: "echtools.op.processActivation",
    subtitle: "POST /v1/process",
    fields: [
      { name: "message", label: "echtools.f.message", kind: "text", required: true },
      { name: "applicationId", label: "echtools.f.applicationId", kind: "text", required: true },
      { name: "pushIdentifier", label: "echtools.f.pushIdentifier", kind: "text", nullable: true },
      { name: "processGuid", label: "echtools.f.processGuid", kind: "text", required: true },
    ],
  },
];

const AUTHORIZATION_OPERATIONS: EchOperationSpec[] = [
  {
    id: "createAuthRequest",
    group: "authorization",
    label: "echtools.op.createAuthRequest",
    subtitle: "POST /v1/authorization/request",
    fields: [
      { name: "signId", label: "echtools.f.signId", kind: "number", required: true },
      { name: "transactionId", label: "echtools.f.transactionId", kind: "text", required: true },
      { name: "requestTypeId", label: "echtools.f.requestTypeId", kind: "number", default: "1", fallback: 1, required: true },
      { name: "device", label: "echtools.f.device", kind: "text", required: true, placeholder: "TDP000001007-1" },
      { name: "secureChannelMessageSignature", label: "echtools.f.secureChannelMessageSignature", kind: "text", nullable: true },
    ],
  },
  {
    id: "confirmAuthRequest",
    group: "authorization",
    label: "echtools.op.confirmAuthRequest",
    subtitle: "PUT /v1/authorization/requests/{guid}/confirm",
    execAs: "confirmTransactionWithOtp",
    needsCacheToken: true,
    fields: [
      { name: "guid", label: "echtools.f.guid", kind: "cacheGuid", required: true },
      { name: "pinType", label: "echtools.f.pinType", kind: "pinRadio", default: "valid" },
    ],
  },
  {
    id: "getAuthDetail",
    group: "authorization",
    label: "echtools.op.getAuthDetail",
    subtitle: "GET /v1/authorization/requests/{guid}/detail",
    fields: [{ name: "guid", label: "echtools.f.guid", kind: "text", required: true }],
  },
  {
    id: "getAuthDetailPost",
    group: "authorization",
    label: "echtools.op.getAuthDetailPost",
    subtitle: "POST /v1/authorization/requests/detail",
    fields: [
      { name: "transactionId", label: "echtools.f.transactionId", kind: "text", nullable: true },
      { name: "signId", label: "echtools.f.signId", kind: "number", nullable: true },
    ],
  },
  {
    id: "getAuthList",
    group: "authorization",
    label: "echtools.op.getAuthList",
    subtitle: "GET /v1/authorization/requests/{deviceType}/{deviceId}",
    fields: [
      { name: "tokenId", label: "echtools.f.tokenId", kind: "tokenSelect", activeOnly: true, required: true },
      { name: "deviceType", label: "echtools.f.deviceType", kind: "number", default: "34", fallback: 34, required: true },
      { name: "ippids", label: "echtools.f.ippids", kind: "text", default: "60322504", required: true },
    ],
  },
  {
    id: "receiveElbCache",
    group: "authorization",
    label: "echtools.op.receiveElbCache",
    subtitle: "POST /v1/authorization/requests/{guid}/elb/response",
    fields: [
      { name: "guid", label: "echtools.f.guid", kind: "text", required: true },
      { name: "transactionId", label: "echtools.f.transactionId", kind: "text", nullable: true },
      { name: "signId", label: "echtools.f.signId", kind: "number", nullable: true },
    ],
  },
  {
    id: "receiveAuthStatus",
    group: "authorization",
    label: "echtools.op.receiveAuthStatus",
    subtitle: "POST /v1/authorization/request/receive",
    fields: [
      { name: "transactionId", label: "echtools.f.transactionId", kind: "text", nullable: true },
      { name: "signId", label: "echtools.f.signId", kind: "number", nullable: true },
    ],
  },
  {
    id: "rejectAuthRequest",
    group: "authorization",
    label: "echtools.op.rejectAuthRequest",
    subtitle: "PUT /v1/authorization/requests/reject",
    autofillFromCache: true,
    fields: [
      { name: "guid", label: "echtools.f.guid", kind: "cacheGuid" },
      { name: "transactionId", label: "echtools.f.transactionId", kind: "text", nullable: true },
      { name: "signId", label: "echtools.f.signId", kind: "number", nullable: true },
      { name: "usedDeviceAuthType", label: "echtools.f.usedDeviceAuthType", kind: "select", default: "1", fallback: 1, options: DEVICE_AUTH_OPTIONS },
    ],
  },
  {
    id: "setOfflineStatus",
    group: "authorization",
    label: "echtools.op.setOfflineStatus",
    subtitle: "PUT /v1/authorization/request/status/set-offline",
    fields: [
      { name: "transactionId", label: "echtools.f.transactionId", kind: "text", required: true },
      { name: "signId", label: "echtools.f.signId", kind: "number", required: true },
      { name: "requestStatusId", label: "echtools.f.requestStatusId", kind: "number", required: true },
      { name: "errorCode", label: "echtools.f.errorCode", kind: "text", nullable: true },
    ],
  },
];

const OTP_OPERATIONS: EchOperationSpec[] = [
  {
    id: "verifyOtp",
    group: "otp",
    label: "echtools.op.verifyOtp",
    subtitle: "GET /v1/authorization/{deviceId}/{otp}/verify",
    fields: [
      { name: "tokenId", label: "echtools.f.tokenId", kind: "tokenSelect", activeOnly: true, required: true },
      { name: "password", label: "echtools.f.password", kind: "password", required: true },
    ],
  },
  {
    id: "validateOtp",
    group: "otp",
    label: "echtools.op.validateOtp",
    subtitle: "GET /v1/authorization/{deviceId}/{otp}/validate",
    fields: [
      { name: "deviceId", label: "echtools.f.deviceId", kind: "text", required: true },
      { name: "otp", label: "echtools.f.otp", kind: "text", required: true, placeholder: "123456" },
    ],
  },
];

const PROFILE_OPERATIONS: EchOperationSpec[] = [
  {
    id: "addIppid",
    group: "profile",
    label: "echtools.op.addIppid",
    subtitle: "PUT /v1/profile/{deviceType}/{deviceId}/addippidtodevice",
    fields: [
      { name: "deviceType", label: "echtools.f.deviceType", kind: "number", default: "1", fallback: 1, required: true },
      { name: "deviceId", label: "echtools.f.deviceId", kind: "text", required: true },
      { name: "newIppidIdToken", label: "echtools.f.newIppidIdToken", kind: "text", required: true },
      { name: "mobileSdkData", label: "echtools.f.mobileSdkData", kind: "textarea", nullable: true },
    ],
  },
  {
    id: "removeIppid",
    group: "profile",
    label: "echtools.op.removeIppid",
    subtitle: "POST /v1/profile/{deviceType}/{deviceId}/removeippidfromdevice",
    fields: [
      { name: "deviceType", label: "echtools.f.deviceType", kind: "number", default: "1", fallback: 1, required: true },
      { name: "deviceId", label: "echtools.f.deviceId", kind: "text", required: true },
      { name: "otp", label: "echtools.f.otp", kind: "text", required: true },
      { name: "request", label: "echtools.f.request", kind: "text", nullable: true },
      { name: "identitiesToRemove", label: "echtools.f.identitiesToRemove", kind: "text", nullable: true },
    ],
  },
];

const DR_OPERATIONS: EchOperationSpec[] = [
  {
    id: "createReplacement",
    group: "deviceReplacement",
    label: "echtools.op.createReplacement",
    subtitle: "POST /v1/device-replacement/request",
    fields: [
      { name: "instanceId", label: "echtools.f.instanceId", kind: "text", required: true },
      { name: "request", label: "echtools.f.request", kind: "text" },
    ],
  },
  {
    id: "processReplacement",
    group: "deviceReplacement",
    label: "echtools.op.processReplacement",
    subtitle: "POST /v1/device-replacement/process",
    fields: [
      { name: "instanceId", label: "echtools.f.instanceId", kind: "text", required: true },
      { name: "operationId", label: "echtools.f.operationId", kind: "text", required: true },
      { name: "request", label: "echtools.f.request", kind: "text" },
    ],
  },
];

const TRANSACTION_OPERATIONS: EchOperationSpec[] = [
  {
    id: "confirmTransaction",
    group: "transaction",
    label: "echtools.op.confirmTransaction",
    subtitle: "POST /v1/transaction-approval/confirm",
    fields: [
      { name: "instanceId", label: "echtools.f.instanceId", kind: "text", required: true },
      { name: "operationId", label: "echtools.f.operationId", kind: "text", required: true },
      { name: "signature", label: "echtools.f.signature", kind: "text", required: true },
      { name: "request", label: "echtools.f.request", kind: "text" },
      { name: "additionalData", label: "echtools.f.additionalData", kind: "text", nullable: true },
    ],
  },
];

const PIN_OPERATIONS: EchOperationSpec[] = [
  {
    id: "displayPinToken",
    group: "displayPin",
    label: "echtools.op.displayPinToken",
    subtitle: "POST /v1/authorization/requests/{guid}/display-pin/token",
    fields: [
      { name: "guid", label: "echtools.f.guid", kind: "text", required: true },
      { name: "transactionId", label: "echtools.f.transactionId", kind: "text", nullable: true },
      { name: "signId", label: "echtools.f.signId", kind: "number", nullable: true },
    ],
  },
];

export const ECH_GROUPS: EchGroupSpec[] = [
  { group: "activation", label: "echtools.group.activation", operations: ACTIVATION_OPERATIONS },
  { group: "authorization", label: "echtools.group.authorization", operations: AUTHORIZATION_OPERATIONS },
  { group: "otp", label: "echtools.group.otp", operations: OTP_OPERATIONS },
  { group: "profile", label: "echtools.group.profile", operations: PROFILE_OPERATIONS },
  { group: "deviceReplacement", label: "echtools.group.deviceReplacement", operations: DR_OPERATIONS },
  { group: "transaction", label: "echtools.group.transaction", operations: TRANSACTION_OPERATIONS },
  { group: "displayPin", label: "echtools.group.displayPin", operations: PIN_OPERATIONS },
];

/**
 * Sestaví request body z hodnot polí operace (mimo subject/accessTokenOverride,
 * které injektuje stránka). pinRadio → `useValidPin` "true"/"false"; number →
 * int/null; nullable text → null. Password (verifyOtp) dořeší runner async.
 */
export function buildEchannelsBody(op: EchOperationSpec, values: Record<string, unknown>): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  for (const f of op.fields) {
    const raw = values[f.name];
    switch (f.kind) {
      case "pinRadio":
        body.useValidPin = (String(raw ?? "valid") === "valid").toString();
        break;
      case "number": {
        const s = String(raw ?? "").trim();
        if (s === "") {
          body[f.name] = f.nullable ? null : f.fallback ?? null;
        } else {
          const n = parseInt(s, 10);
          body[f.name] = Number.isNaN(n) ? (f.nullable ? null : f.fallback ?? null) : n;
        }
        break;
      }
      case "password":
        // PasswordField vyplní hodnotu přímo (valid/invalid) → posílá se jako string.
        body[f.name] = String(raw ?? "");
        break;
      case "text":
      case "textarea":
      case "uuid": {
        const s = String(raw ?? "");
        body[f.name] = s === "" && f.nullable ? null : s;
        break;
      }
      default:
        // select | tokenSelect | cacheGuid
        body[f.name] = String(raw ?? "");
    }
  }
  return body;
}
